import * as v from 'valibot';

/**
 * One child a `forEach` step materialized.
 *
 * The item is stored rather than derived, for two reasons that both come up in
 * practice: a failed child can be retried from exactly the input it had, and a
 * run board can say "failed on acme/legacy-api" instead of showing forty rows
 * with the same step id.
 */
export const FanOutChild = v.object({
  id: v.string(),
  runId: v.string(),
  parentStepId: v.string(),
  childStepId: v.string(),
  /** Position in the source list, so children sort the way the list was written. */
  ordinal: v.pipe(v.number(), v.integer()),
  item: v.unknown(),
  label: v.nullable(v.string()),
  status: v.string(),
  spendCents: v.number(),
  error: v.nullable(v.string()),
  createdAt: v.string(),
});
export type FanOutChild = v.InferOutput<typeof FanOutChild>;

/**
 * Resolve a dotted path against a value.
 *
 * Shares the trigger matcher's vocabulary on purpose: `pull_request.base.ref`
 * means the same thing in a fan-out source as it does in a payload condition,
 * and somebody who has learned one should not have to learn the other.
 */
export function resolvePath(value: unknown, path: string): unknown {
  return path
    .split('.')
    .filter(Boolean)
    .reduce<unknown>((current, segment) => {
      if (current === null || current === undefined) return undefined;
      if (Array.isArray(current)) {
        const index = Number.parseInt(segment, 10);
        return Number.isNaN(index) ? undefined : current[index];
      }
      if (typeof current === 'object') return (current as Record<string, unknown>)[segment];
      return undefined;
    }, value);
}

/**
 * A short human label for one item.
 *
 * Falls through the fields that are almost always what somebody meant — a name,
 * a title, an id — before giving up and stringifying. Giving up produces
 * something truncated rather than a wall of JSON, because this lands in a table
 * cell next to a status.
 */
export function labelFor(item: unknown, path?: string): string {
  if (path) {
    const picked = resolvePath(item, path);
    if (typeof picked === 'string' || typeof picked === 'number') return String(picked);
  }
  if (typeof item === 'string' || typeof item === 'number') return String(item);
  if (item && typeof item === 'object') {
    for (const key of ['full_name', 'name', 'title', 'id', 'slug', 'path']) {
      const candidate = (item as Record<string, unknown>)[key];
      if (typeof candidate === 'string' || typeof candidate === 'number') return String(candidate);
    }
  }
  return JSON.stringify(item ?? null).slice(0, 60);
}

/**
 * Split a parent's budget across the children it is about to create.
 *
 * The same monotonic rule the policy chain follows, applied to spend: children
 * divide what the parent has rather than each being granted it afresh. Without
 * this, fanning out forty ways multiplies a workflow's spend ceiling by forty,
 * and the deployment ceiling is the only thing left between a mistyped `source`
 * path and the month's budget.
 */
export function carveBudget(
  parentCents: number,
  childCount: number,
): { perChild: number; reserved: number } {
  if (childCount <= 0) return { perChild: 0, reserved: parentCents };
  // A tenth held back for the parent: it still has to read the results and say
  // something about them after the last child finishes.
  const reserved = Math.max(1, Math.floor(parentCents * 0.1));
  const perChild = Math.max(1, Math.floor((parentCents - reserved) / childCount));
  return { perChild, reserved };
}

/**
 * The id of the child a fan-out creates for one position in its list.
 *
 * Derived from the parent and the ordinal rather than generated, because a
 * parent that crashed after dispatching half its children and was retried must
 * address the same children again. A random id would hand the retry a second
 * set: forty repositories migrated twice, the first forty results orphaned in a
 * conversation nobody holds the id of any more.
 *
 * It lives here rather than beside the runtime that mints it because the *shape*
 * is load-bearing outside the runtime: `resolveStep` has to recognise one to
 * resolve a child against its parent's configuration, and a child id is a step
 * id everywhere else in reef — in a conversation id, on a run's timeline, in an
 * audit row.
 */
export function childStepId(parentStepId: string, ordinal: number): string {
  return `${parentStepId}.${ordinal}`;
}

/**
 * Read a fan-out child id back into the parent it belongs to.
 *
 * Null for anything that is not one, which is the common case — every authored
 * step id reaches this. The suffix must be digits: a step somebody named
 * `release.v2` is an authored step, not child two of `release`, and treating it
 * as one would resolve it against a parent that does not exist.
 */
export function parseChildStepId(stepId: string): { parentStepId: string; ordinal: number } | null {
  const match = /^(.+)\.(\d+)$/.exec(stepId);
  if (!match) return null;
  return { parentStepId: match[1]!, ordinal: Number.parseInt(match[2]!, 10) };
}
